import { Text, Badge, Stack, Group } from "@mantine/core"
import type { CertificateStatus } from "@tfg/db"
import { CopyButton } from "@tfg/ui"
import type { TFunction } from "i18next"
import { useTranslation } from "react-i18next"

import { DocumentActionsCell } from "~/components/cells/DocumentActionsCell"
import { FindCertificatesResponseDto } from "~/gen/api"
import { useAuth } from "~/store/auth-context"

interface CertificateViewerProps {
  certificate: Exclude<FindCertificatesResponseDto, null>
}

export const CertificateViewer = ({ certificate }: CertificateViewerProps) => {
  const { t } = useTranslation(["traceability"])
  const { isAdmin } = useAuth()

  const badgeColors: { [K in CertificateStatus]: string } = {
    NOT_UPLOADED: "gray",
    REJECTED: "yellow",
    PENDING: "orange",
    PROCESSED: "green",
  } as const

  const badgeStatus: { [K in CertificateStatus]: ReturnType<TFunction> } = {
    NOT_UPLOADED: t("status.NOT_UPLOADED"),
    REJECTED: t("status.REJECTED"),
    PENDING: t("status.UPLOADED"),
    PROCESSED: t("status.PROCESSED"),
  } as const

  return (
    <Stack gap={2}>
      <CopyButton
        label="Certificate Id"
        size="xs"
        copyableText={certificate.id}
        admin={isAdmin}
      />
      <Group gap="xs">
        <Text>{certificate.type.name}</Text>
        {certificate.reference ? (
          <Text fw={700}>
            {t("certificates.reference", {
              reference: certificate.reference,
            })}
          </Text>
        ) : null}
        <Group gap="xs">
          <Badge color={badgeColors[certificate.status]}>
            {badgeStatus[certificate.status]}
          </Badge>
          <DocumentActionsCell
            documentName={certificate.type.name}
            documentId={certificate.document.id}
          />
          {certificate.rejectionMessage ? (
            <Text c="red">
              <Text c="red" component="span" fw={700}>
                {t("certificates.rejectionMessage")} :{" "}
              </Text>
              {certificate.rejectionMessage}
            </Text>
          ) : null}
        </Group>
      </Group>
    </Stack>
  )
}
